import type { SelectOption } from '@shared/types/room';

interface OptionGridProps {
  options: SelectOption[];
  disabled: boolean;
  highlightId: string | null;
  landed: boolean;
  onSelect: (option: SelectOption) => void;
  large?: boolean;
}

export function OptionGrid({ options, disabled, highlightId, landed, onSelect, large = false }: OptionGridProps) {
  const cols = large ? 'grid-cols-1 sm:grid-cols-2' : options.length <= 4 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';
  return (
    <ul className={`grid gap-3 ${cols}`} role="list">
      {options.map((option) => {
        const highlighted = highlightId === option.id;
        const dimmed = highlightId !== null && !highlighted && landed;
        return (
          <li key={option.id} className="animate-fade-up">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onSelect(option)}
              aria-pressed={highlighted && landed}
              className={`group flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 bg-white text-center transition-all duration-150 ${
                large ? 'min-h-32 px-4 py-6' : 'min-h-24 px-3 py-4'
              } ${
                highlighted
                  ? landed
                    ? 'scale-[1.03] border-primary-500 bg-primary-50 shadow-(--shadow-card) ring-4 ring-primary-100'
                    : 'border-accent-400 bg-accent-300/40'
                  : 'border-cream-300'
              } ${dimmed ? 'opacity-40' : ''} ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-(--shadow-soft) active:scale-[0.98]'
              } focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-200`}
            >
              <span className={large ? 'text-4xl' : 'text-3xl'} aria-hidden="true">
                {option.emoji}
              </span>
              <span className={`font-bold leading-tight break-keep ${large ? 'text-lg' : 'text-base'}`}>{option.name}</span>
              {option.description && <span className="text-xs text-charcoal-500">{option.description}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
